import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImagePlus, X } from 'lucide-react';
import type { MediaRef } from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { SortableList } from '@/components/ui/SortableList.js';
import { mediaApi } from '@/features/media/media-api.js';
import { MediaPickerModal, MediaThumb } from './MediaPicker.js';

export type MediaGalleryPickerProps = {
  label: string;
  value: readonly MediaRef[];
  onChange: (value: MediaRef[]) => void;
  max?: number;
  hint?: string;
};

/**
 * Ko'p rasmli tanlov (galereya, portfolio).
 *
 * Tartib MUHIM — saytda aynan shu ketma-ketlikda chiqadi, shuning uchun
 * surib o'zgartiriladi. Bitta-bitta tanlash o'rniga modaldan ketma-ket
 * qo'shiladi: 20 ta rasmni yuklash uchun modal har safar yopilib
 * ochilaverishi charchatardi.
 */
export function MediaGalleryPicker({
  label,
  value,
  onChange,
  max = 40,
  hint,
}: MediaGalleryPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-n-800">
          {label}
          <span className="ml-1.5 text-xs font-normal text-n-400">
            {value.length}/{max}
          </span>
        </span>
        <Button
          size="sm"
          variant="outline"
          icon={<ImagePlus className="size-4" />}
          disabled={value.length >= max}
          onClick={() => setOpen(true)}
        >
          Rasm qo‘shish
        </Button>
      </div>

      {value.length === 0 ? (
        <p className=" border border-dashed border-n-200 px-3 py-6 text-center text-sm text-n-600">
          Hozircha rasm yo‘q
        </p>
      ) : (
        <SortableList
          items={value as MediaRef[]}
          getId={(item) => item.mediaId}
          onReorder={onChange}
          renderRow={(item) => (
            <div className="flex flex-1 items-center gap-3">
              <Thumb mediaId={item.mediaId} />
              <Button
                size="sm"
                variant="ghost"
                aria-label="Olib tashlash"
                className="ml-auto"
                onClick={() => onChange(value.filter((row) => row.mediaId !== item.mediaId))}
              >
                <X className="size-4" aria-hidden />
              </Button>
            </div>
          )}
        />
      )}

      {hint && <p className="text-sm text-n-600">{hint}</p>}

      <MediaPickerModal
        open={open}
        kind="image"
        onClose={() => setOpen(false)}
        onSelect={(item) => {
          // Bir xil rasmni ikki marta qo'shish galereyada dublikat
          // yaratardi va sayt uni ikki marta ko'rsatardi.
          if (value.some((row) => row.mediaId === item._id)) return;
          onChange([...value, { mediaId: item._id, alt: null }]);
        }}
      />
    </div>
  );
}

function Thumb({ mediaId }: { mediaId: string }) {
  const { data } = useQuery({
    queryKey: ['media', 'one', mediaId],
    queryFn: () => mediaApi.getOne(mediaId),
  });

  const item = data?.data;

  return (
    <>
      <div className="size-10 shrink-0 overflow-hidden bg-n-100">
        {item && <MediaThumb item={item} className="size-10" />}
      </div>
      <span className="truncate text-sm text-n-800">{item?.originalName ?? '…'}</span>
    </>
  );
}
